import {engineContext} from './context.js';

(function(global){
'use strict';
const VERSION='0.1.0-alpha';

const WEIGHT=Object.freeze({
  BUSINESS:1.00,
  STOCK:0.85,
  TIMING:0.65,
  WHY_WRONG:1.00,
  EVIDENCE:0.90,
  MACRO:0.45,
  EVENT:0.55,
  ECOSYSTEM:0.75,
  THESIS:1.00,
  UNKNOWN:0.40
});

function norm(x){
  x=x||{};
  const domain=Object.prototype.hasOwnProperty.call(WEIGHT,x.domain)?x.domain:'UNKNOWN';
  return Object.freeze({
    key:x.key||null,
    domain,
    direction:['POSITIVE','NEGATIVE','MIXED'].includes(x.direction)?x.direction:'MIXED',
    magnitude:Number.isFinite(x.magnitude)?Math.max(0,Math.min(1,x.magnitude)):0,
    verification:['VERIFIED','PARTIAL','UNVERIFIED'].includes(x.verification)?x.verification:'UNVERIFIED',
    sourceFamily:x.sourceFamily||null,
    freshness:Number.isFinite(x.freshness)?Math.max(0,Math.min(1,x.freshness)):1
  });
}

function evidenceFactor(x){
  const n=norm(x);
  return n.verification==='VERIFIED'?1:n.verification==='PARTIAL'?.6:.25;
}

function contribution(x){
  const n=norm(x);
  return n.magnitude*WEIGHT[n.domain]*evidenceFactor(n)*n.freshness;
}

function independentFamilies(items){
  return new Set((Array.isArray(items)?items:[]).map(norm).filter(x=>x.sourceFamily).map(x=>x.sourceFamily)).size;
}

function compound(items){
  const rows=(Array.isArray(items)?items:[]).map(norm);
  const verifiedOrPartial=rows.filter(x=>x.verification!=='UNVERIFIED');
  const neg=verifiedOrPartial.filter(x=>x.direction==='NEGATIVE');
  const pos=verifiedOrPartial.filter(x=>x.direction==='POSITIVE');
  const negDomains=new Set(neg.map(x=>x.domain)).size;
  const posDomains=new Set(pos.map(x=>x.domain)).size;
  const negFamilies=independentFamilies(neg);
  const posFamilies=independentFamilies(pos);
  const negWeight=neg.reduce((a,x)=>a+contribution(x),0);
  const posWeight=pos.reduce((a,x)=>a+contribution(x),0);

  let state='NO_COMPOUND_SIGNAL';
  if(negDomains>=3 && negFamilies>=2 && negWeight>=1.2) state='COMPOUND_NEGATIVE';
  else if(posDomains>=3 && posFamilies>=2 && posWeight>=1.2) state='COMPOUND_POSITIVE';
  else if(negWeight>=.7 && posWeight>=.7) state='COMPOUND_MIXED';
  else if(negDomains>=2 && negWeight>=.65) state='BUILDING_NEGATIVE';
  else if(posDomains>=2 && posWeight>=.65) state='BUILDING_POSITIVE';

  return Object.freeze({
    state,
    negativeWeight:+negWeight.toFixed(3),
    positiveWeight:+posWeight.toFixed(3),
    negativeDomains:negDomains,
    positiveDomains:posDomains,
    negativeIndependentFamilies:negFamilies,
    positiveIndependentFamilies:posFamilies,
    universalScore:null,
    tradeSignal:false,
    capitalPermission:false,
    companyQualityOverride:false
  });
}

function explain(items){
  const c=compound(items);
  const map={
    COMPOUND_NEGATIVE:'Several independent, evidence-backed negative changes now reinforce each other across different domains.',
    COMPOUND_POSITIVE:'Several independent, evidence-backed positive changes now reinforce each other across different domains.',
    COMPOUND_MIXED:'Material positive and negative changes coexist; do not average the contradiction away.',
    BUILDING_NEGATIVE:'Negative evidence is beginning to cluster, but compound confirmation is incomplete.',
    BUILDING_POSITIVE:'Positive evidence is beginning to cluster, but compound confirmation is incomplete.',
    NO_COMPOUND_SIGNAL:'No sufficiently independent multi-domain compound signal is established.'
  };
  return Object.freeze({state:c.state,text:map[c.state],tradeSignal:false});
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  const neg=[
    {domain:'BUSINESS',direction:'NEGATIVE',magnitude:.8,verification:'VERIFIED',sourceFamily:'A'},
    {domain:'THESIS',direction:'NEGATIVE',magnitude:.8,verification:'VERIFIED',sourceFamily:'B'},
    {domain:'EVIDENCE',direction:'NEGATIVE',magnitude:.8,verification:'VERIFIED',sourceFamily:'C'}
  ];
  ok('three independent negative domains compound',compound(neg).state==='COMPOUND_NEGATIVE');
  ok('compound negative uses multiple domains',compound(neg).negativeDomains===3);
  ok('compound negative uses independent families',compound(neg).negativeIndependentFamilies===3);

  const same=neg.map(x=>({...x,sourceFamily:'A'}));
  ok('same source family cannot fully compound',compound(same).state!=='COMPOUND_NEGATIVE');

  const unver=neg.map(x=>({...x,verification:'UNVERIFIED'}));
  ok('unverified signals cannot compound',compound(unver).state==='NO_COMPOUND_SIGNAL');

  const pos=neg.map((x,i)=>({...x,direction:'POSITIVE',sourceFamily:String(i)}));
  ok('independent positive domains can compound',compound(pos).state==='COMPOUND_POSITIVE');

  const mixed=[...neg.slice(0,2),...pos.slice(0,2)];
  ok('material opposing evidence stays mixed',compound(mixed).state==='COMPOUND_MIXED');

  ok('freshness reduces contribution',contribution({...neg[0],freshness:.2})<contribution(neg[0]));
  ok('partial evidence weighs less than verified',contribution({...neg[0],verification:'PARTIAL'})<contribution(neg[0]));
  ok('compound engine has no universal score',compound(neg).universalScore===null);
  ok('compound engine emits no trade signal',compound(neg).tradeSignal===false);
  ok('compound engine grants no capital permission',compound(neg).capitalPermission===false);
  ok('compound engine cannot overwrite company quality',compound(neg).companyQualityOverride===false);
  ok('explanation exists for compound state',explain(neg).text.length>0);

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

const API={VERSION,WEIGHT,norm,evidenceFactor,contribution,independentFamilies,compound,explain,selfTest};
try{API.selfTestResult=selfTest()}catch(e){API.bootstrapError=String(e&&e.stack||e)}
global.ZooInvestCompoundSignalEngine=Object.freeze(API);
})(engineContext);
